import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import ChatMessage from './components/ChatMessage';
import ChatInput, { Attachment } from './components/ChatInput';
import TopStrip from './components/TopStrip';
import ModulRail from './components/ModulRail';
import ThinkingIndicator from './components/ThinkingIndicator';
import ManagementPage from './pages/ManagementPage';
import LoginPage from './pages/LoginPage';
import RegistrierenPage from './pages/RegistrierenPage';
import PasswortVergessenPage from './pages/PasswortVergessenPage';
import SuperAdminPage from './pages/SuperAdminPage';
import WarnungenPanel from './components/WarnungenPanel';
import SplitWorkspace from './components/SplitWorkspace';
import CommandPalette from './components/CommandPalette';
import SchnellanfragenChips from './components/SchnellanfragenChips';
import SettingsDrawer from './components/settings/SettingsDrawer';
import { Message, ChatResponse, Warnung } from './types';
import { AuthUser } from './types/auth';
import { TraceGroup } from './types/panel';
import './App.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface KonversationInfo {
  konversation_id: number;
  titel: string;
  erstellt_am: string;
  anzahl_nachrichten: number;
}

// Leerer Verlauf zeigt die Kontor-Hero-Begrüßung (k-hello) statt einer Pseudo-Nachricht

function App() {
  // Auth State – aus localStorage wiederherstellen
  const [authToken, setAuthToken] = useState<string | null>(
    () => localStorage.getItem('auth_token')
  );
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem('auth_user');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return {
      ...parsed,
      tenant_id: parsed.tenant_id ?? null,
      tenant_slug: parsed.tenant_slug ?? null,
      firmenname: parsed.firmenname ?? null,
      primaerfarbe: parsed.primaerfarbe ?? null,
    };
  });

  const [currentView, setCurrentView] = useState<'chat' | 'management' | 'superadmin'>('chat');
  const [managementTab, setManagementTab] = useState<string | undefined>(undefined);
  // Bühne offen? (steuert die is-staged Choreografie am k-app Root)
  const [staged, setStaged] = useState(false);
  // Zuletzt über die Modul-Rail geöffneter Verwaltungsbereich
  const [railTab, setRailTab] = useState<string | null>(null);
  // Stimmung (hell/dunkel/mix) — Umschalter im Top-Strip, persistiert
  const [mood, setMood] = useState<string>(() => localStorage.getItem('kontor-mood') ?? 'hell');
  const cycleMood = useCallback(() => {
    setMood(m => {
      const order = ['hell', 'dunkel', 'mix'];
      const next = order[(order.indexOf(m) + 1) % order.length];
      localStorage.setItem('kontor-mood', next);
      return next;
    });
  }, []);

  const handleViewChange = useCallback((view: 'chat' | 'management' | 'superadmin') => {
    setCurrentView(view);
    if (view === 'chat') setRailTab(null);
  }, []);

  const handleRailNavigate = useCallback((tab: string) => {
    setCurrentView('management');
    setManagementTab(tab);
    setRailTab(tab);
  }, []);

  const handleCommandNavigate = useCallback((view: 'chat' | 'management', tab?: string) => {
    setCurrentView(view);
    if (tab) setManagementTab(tab);
    if (view === 'chat') setRailTab(null);
  }, []);
  const handleTabConsumed = useCallback(() => setManagementTab(undefined), []);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Gruppierter Tool-Trace (k-trace) + Statuszeile
  const [traceGroups, setTraceGroups] = useState<TraceGroup[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  // Laufzeit des abgeschlossenen Laufs (für die Einzeiler-Zusammenfassung)
  const [traceDuration, setTraceDuration] = useState(0);
  const handleThinking = useCallback((groups: TraceGroup[], active: boolean, durationSeconds?: number) => {
    setTraceGroups(groups);
    setIsThinking(active);
    if (durationSeconds !== undefined) setTraceDuration(durationSeconds);
  }, []);
  // Live-Reasoning (reasoning_delta): '' blendet die Zeile aus
  const [reasoningText, setReasoningText] = useState('');
  const handleReasoning = useCallback((text: string) => setReasoningText(text), []);
  // Finales Reasoning nach Abschluss („Gedacht" im aufgeklappten Trace)
  const [finalReasoning, setFinalReasoning] = useState('');
  const handleReasoningFinal = useCallback((text: string) => setFinalReasoning(text), []);
  // Abbruch-Funktion des laufenden Streams (von SplitWorkspace bereitgestellt)
  const [abortFn, setAbortFn] = useState<(() => void) | null>(null);
  const handleAbortReady = useCallback((fn: () => void) => setAbortFn(() => fn), []);
  // Bühnen-Tab aus einer Nachricht heraus öffnen
  const [openStageFn, setOpenStageFn] = useState<((ref: string) => void) | null>(null);
  const handleOpenStageReady = useCallback(
    (fn: (ref: string) => void) => setOpenStageFn(() => fn),
    []
  );
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [konversationen, setKonversationen] = useState<KonversationInfo[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Token-Streaming: Id der gerade live wachsenden Assistant-Nachricht (oder null)
  const streamingIdRef = useRef<string | null>(null);

  // Warnungen State
  const [warnungen, setWarnungen] = useState<Warnung[]>([]);
  const [showWarnungen, setShowWarnungen] = useState(false);

  // SSE Streaming sender (provided by SplitWorkspace via onReady)
  const [streamSend, setStreamSend] = useState<((c: string, attachments?: Attachment[]) => Promise<void>) | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleStreamReady = useCallback(
    (fn: (c: string, attachments?: Attachment[]) => Promise<void>) => setStreamSend(() => fn),
    []
  );

  // Stabile Identität: SplitWorkspace leitet diese Callbacks über useCallback-
  // Ketten in den onReady-Effekt; inline-Arrows würden bei jedem Render eine
  // neue sendMessage-Identität erzeugen → Endlos-Render-Loop (Browser-OOM).
  // Token-Streaming: laufende Antwort live aktualisieren. fullText ist der
  // gesamte bisher gestreamte Text des aktuellen Turns (kann '' sein = Reset).
  const handleAssistantDelta = useCallback((fullText: string) => {
    if (streamingIdRef.current === null) {
      if (!fullText) return; // leeres Reset ohne laufende Nachricht: nichts tun
      const id = `stream-${Date.now()}`;
      streamingIdRef.current = id;
      setMessages(prev => [...prev, {
        id, role: 'assistant', content: fullText, timestamp: new Date(),
      }]);
      return;
    }
    const id = streamingIdRef.current;
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, content: fullText } : m)));
  }, []);

  const handleAssistantMessage = useCallback(
    (text: string, agent?: string, panel?: { ref: string; title: string }) => {
      // Lief ein Stream? Dann die gewachsene Nachricht autoritativ finalisieren
      // statt eine zweite anzuhängen.
      if (streamingIdRef.current !== null) {
        const id = streamingIdRef.current;
        streamingIdRef.current = null;
        setMessages(prev => prev.map(m => (
          m.id === id
            ? { ...m, content: text, agent, panelRef: panel?.ref, panelTitle: panel?.title }
            : m
        )));
        return;
      }
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(), role: 'assistant',
        content: text, timestamp: new Date(), agent,
        panelRef: panel?.ref, panelTitle: panel?.title
      };
      setMessages(prev => [...prev, assistantMessage]);
    },
    []
  );

  const handleStreamError = useCallback((msg: string) => {
    streamingIdRef.current = null;
    const errorMessage: Message = {
      id: (Date.now() + 1).toString(), role: 'assistant',
      content: `Fehler: ${msg}`, timestamp: new Date()
    };
    setMessages(prev => [...prev, errorMessage]);
  }, []);

  // Stop-Button: Stream abbrechen und die teilweise gestreamte Nachricht als
  // abgeschlossen behandeln — sonst würde die NÄCHSTE Antwort in die alte
  // Nachricht hineinwachsen (streamingIdRef zeigt noch auf sie).
  const handleAbort = useCallback(() => {
    abortFn?.();
    streamingIdRef.current = null;
  }, [abortFn]);

  // Sekundenzähler für die Statuszeile: läuft solange der Agent arbeitet
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const busy = isThinking || isLoading;

  // Statuszeile: aktiver Agent = letzte running-Gruppe (Fallback "Agent"),
  // letzter Step bevorzugt aus dieser Gruppe, sonst letzter Step überhaupt
  const runningGroup = [...traceGroups].reverse().find(g => g.status === 'running');
  const activeAgentName = runningGroup?.agent ?? 'Agent';
  let lastTraceStep = '';
  if (runningGroup && runningGroup.steps.length > 0) {
    lastTraceStep = runningGroup.steps[runningGroup.steps.length - 1];
  } else {
    for (let i = traceGroups.length - 1; i >= 0; i--) {
      const g = traceGroups[i];
      if (g.steps.length > 0) { lastTraceStep = g.steps[g.steps.length - 1]; break; }
    }
  }
  useEffect(() => {
    if (!busy) return;
    setElapsedSeconds(0);
    const interval = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [busy]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, traceGroups]);

  // Globaler Drop-Schutz: eine neben einer Dropzone fallengelassene Datei
  // würde der Browser sonst als Navigation öffnen und die Seite ersetzen.
  useEffect(() => {
    const verhindereStandardDrop = (e: globalThis.DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener('dragover', verhindereStandardDrop);
    window.addEventListener('drop', verhindereStandardDrop);
    return () => {
      window.removeEventListener('dragover', verhindereStandardDrop);
      window.removeEventListener('drop', verhindereStandardDrop);
    };
  }, []);

  // Tenant-Primärfarbe wird bewusst NICHT mehr ins Layout injiziert:
  // Das Kontor-Design erlaubt ausschließlich Token-Farben (siehe Migrationsplan).

  // Login handler
  const handleLogin = (token: string, user: AuthUser) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
    setAuthToken(token);
    setAuthUser(user);
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setAuthToken(null);
    setAuthUser(null);
    setConversationId(null);
    setMessages([]);
    setWarnungen([]);
  };

  // Warnungen laden (Polling alle 60s)
  const loadWarnungen = useCallback(async () => {
    try {
      const res = await axios.get<Warnung[]>(`${API_URL}/api/warnungen?nur_ungelesen=true`);
      setWarnungen(res.data);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    if (!authToken) return;
    loadWarnungen();
    const interval = setInterval(loadWarnungen, 60_000);
    return () => clearInterval(interval);
  }, [authToken, loadWarnungen]);

  const handleMarkRead = async (id: number) => {
    try {
      await axios.put(`${API_URL}/api/warnungen/${id}/lesen`);
      setWarnungen(prev => prev.filter(w => w.warnung_id !== id));
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/warnungen/alle-lesen`);
      setWarnungen([]);
    } catch { /* ignore */ }
  };

  // Konversationsliste laden
  const loadKonversationen = useCallback(async () => {
    try {
      const res = await axios.get<KonversationInfo[]>(`${API_URL}/api/konversationen?limit=15`);
      setKonversationen(res.data);
    } catch {
      // Silently fail – History ist optional
    }
  }, []);

  useEffect(() => {
    if (showHistory) loadKonversationen();
  }, [showHistory, loadKonversationen]);

  // Bestehende Konversation laden
  const loadKonversation = async (id: number) => {
    try {
      const res = await axios.get(`${API_URL}/api/konversationen/${id}`);
      const nachrichten: Message[] = res.data.nachrichten.map((n: any) => ({
        id: n.nachricht_id.toString(),
        role: n.rolle as 'user' | 'assistant',
        content: n.inhalt,
        timestamp: new Date(n.erstellt_am),
        agent: n.agent_genutzt
      }));
      setMessages(nachrichten);
      setConversationId(id);
      setShowHistory(false);
    } catch {
      console.error('Fehler beim Laden der Konversation');
    }
  };

  // Neue Konversation starten
  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
  };

  const handleSendMessage = async (content: string) => {
    setIsLoading(true);

    try {
      const response = await axios.post<ChatResponse>(`${API_URL}/api/chat`, {
        message: content,
        conversation_id: conversationId,
        user_role: authUser?.rolle
      });

      if (response.data.conversation_id && !conversationId) {
        setConversationId(response.data.conversation_id);
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        agent: response.data.agent_used
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Fehler beim Senden:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte stelle sicher, dass der Backend-Server läuft und die Anthropic API Key konfiguriert ist.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // Public Route: Einladungs-Link einlösen (kein Login nötig)
  if (window.location.pathname === '/registrieren') {
    return <RegistrierenPage />;
  }

  // Public Route: Passwort-Reset anfordern (kein Login nötig)
  if (window.location.pathname === '/passwort-vergessen') {
    return <PasswortVergessenPage />;
  }

  // Nicht eingeloggt → LoginPage
  if (!authToken || !authUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div
      className={'k-app' + (currentView === 'chat' && staged ? ' is-staged' : '')}
      data-mood={mood}
      data-anim="inszeniert"
      data-density="luftig"
    >
      <CommandPalette onNavigate={handleCommandNavigate} />
      <TopStrip
        currentView={currentView}
        onViewChange={handleViewChange}
        user={authUser}
        onLogout={handleLogout}
        warnungenAnzahl={warnungen.length}
        onWarnungenClick={() => setShowWarnungen(v => !v)}
        onSettingsClick={() => setSettingsOpen(true)}
        mood={mood}
        onMoodToggle={cycleMood}
      />
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        authToken={authToken ?? ''}
        tenantId={authUser?.tenant_id ?? null}
        userRolle={authUser?.rolle ?? ''}
        authUser={authUser}
        onOpenSuperAdmin={() => { setSettingsOpen(false); setCurrentView('superadmin'); }}
      />

      {showWarnungen && (
        <WarnungenPanel
          warnungen={warnungen}
          onClose={() => setShowWarnungen(false)}
          onMarkRead={handleMarkRead}
          onMarkAllRead={handleMarkAllRead}
        />
      )}

      <div className="k-main">
        <ModulRail activeTab={currentView === 'management' ? railTab : null} onNavigate={handleRailNavigate} />

        {currentView === 'superadmin' && authUser.rolle === 'SuperAdmin' ? (
          <SuperAdminPage authUser={authUser} authToken={authToken || ''} />
        ) : currentView === 'chat' ? (
          <SplitWorkspace
            authToken={authToken ?? ''}
            conversationId={conversationId}
            onConversationId={setConversationId}
            onStagedChange={setStaged}
            onAssistantMessage={handleAssistantMessage}
            onAssistantDelta={handleAssistantDelta}
            onThinking={handleThinking}
            onReasoning={handleReasoning}
            onReasoningFinal={handleReasoningFinal}
            onOpenStageReady={handleOpenStageReady}
            onAbortReady={handleAbortReady}
            onError={handleStreamError}
            onLoadingChange={setIsLoading}
            onReady={handleStreamReady}
          >
            <div className="chat-container">
              <div className="chat-toolbar">
                <button className="toolbar-btn new-chat-btn" onClick={startNewConversation}>
                  + Neue Konversation
                </button>
                <button
                  className={`toolbar-btn history-btn ${showHistory ? 'active' : ''}`}
                  onClick={() => setShowHistory(v => !v)}
                >
                  Verlauf {conversationId ? `(#${conversationId})` : ''}
                </button>
              </div>

              {showHistory && (
                <div className="history-panel">
                  <div className="history-header">
                    <span>Letzte Konversationen</span>
                    <button className="history-close" onClick={() => setShowHistory(false)}>✕</button>
                  </div>
                  {konversationen.length === 0 ? (
                    <p className="history-empty">Noch keine gespeicherten Konversationen</p>
                  ) : (
                    <ul className="history-list">
                      {konversationen.map(k => (
                        <li
                          key={k.konversation_id}
                          className={`history-item ${k.konversation_id === conversationId ? 'active' : ''}`}
                          onClick={() => loadKonversation(k.konversation_id)}
                        >
                          <span className="history-titel">{k.titel || 'Unbenannte Konversation'}</span>
                          <span className="history-meta">{formatDate(k.erstellt_am)} · {k.anzahl_nachrichten} Nachrichten</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {messages.length === 0 && !isThinking ? (
                <div className="k-hello">
                  <span className="k-hello__eyebrow">Kontor / Agentisches ERP</span>
                  <h1 className="k-hello__h1">Fragen Sie Ihr <em>Unternehmen</em>.</h1>
                  <p className="k-hello__lede">
                    Keine Masken, keine Menüpfade. Der Agent liest Ihre ERP-Daten,
                    rechnet, und legt Ihnen das Ergebnis <strong>auf die Bühne</strong> —
                    jede Aktion im Backend bleibt sichtbar und wartet auf Ihre Freigabe.
                  </p>
                </div>
              ) : (
                <div className="k-msgs messages">
                  {messages.map(message => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      onOpenStage={openStageFn ?? undefined}
                      stageOpenRef={null}
                    />
                  ))}
                  {(isThinking || traceGroups.length > 0) && (
                    <ThinkingIndicator
                      groups={traceGroups}
                      isActive={isThinking}
                      reasoningText={reasoningText}
                      finalReasoning={finalReasoning}
                      durationSeconds={traceDuration}
                    />
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              <div className="k-composer">
                <SchnellanfragenChips
                  authToken={authToken ?? ''}
                  onScenarioClick={(text) => {
                    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: text, timestamp: new Date() }
                    setMessages(prev => [...prev, userMsg])
                    if (streamSend) {
                      streamSend(text)
                    } else {
                      handleSendMessage(text)
                    }
                  }}
                />
                <ChatInput
                  onSendMessage={(content, attachments) => {
                    const userMsg: Message = { id: Date.now().toString(), role: 'user', content, timestamp: new Date() }
                    setMessages(prev => [...prev, userMsg])
                    if (streamSend) {
                      streamSend(content, attachments)
                    } else {
                      handleSendMessage(content)
                    }
                  }}
                  disabled={isLoading}
                  onAbort={abortFn ? handleAbort : undefined}
                />
                <div className={'k-status' + (busy ? ' is-busy' : '')}>
                  <span className="k-status__dot"></span>
                  <span className="k-status__text">
                    {busy
                      ? `${activeAgentName} arbeitet (${elapsedSeconds} s)`
                        + (isThinking && lastTraceStep ? ` — ${lastTraceStep}` : '')
                      : 'Agent bereit'}
                  </span>
                </div>
              </div>
            </div>
          </SplitWorkspace>
        ) : (
          <ManagementPage userRolle={authUser.rolle} authToken={authToken || ''} authUser={authUser} initialTab={managementTab} onInitialTabConsumed={handleTabConsumed} />
        )}
      </div>
    </div>
  );
}

export default App;
